import React, { useState } from 'react';
import { 
  ShieldCheck, Lock, User, Key, ArrowRight, CheckCircle2, 
  AlertCircle, Sparkles, ArrowLeft, Eye, EyeOff, ShieldAlert,
  Award, Building2, Landmark, FileText, ChevronRight
} from 'lucide-react';
import { AUTH_CREDENTIALS, USER_ROLES } from '../../../data/lmsAuthData';

export const DirectorAuthPage = ({ onLoginSuccess, setActiveView }) => {
  const directorPreset = AUTH_CREDENTIALS.find(c => c.role === USER_ROLES.DIRECTOR) || AUTH_CREDENTIALS[0];

  const [username, setUsername] = useState(directorPreset.demoUsername);
  const [password, setPassword] = useState(directorPreset.demoPassword);
  const [directorateToken, setDirectorateToken] = useState('AKSU-EXEC-2026-DIR');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const handle1ClickFastPass = () => {
    setUsername(directorPreset.demoUsername);
    setPassword(directorPreset.demoPassword);
    setDirectorateToken('AKSU-EXEC-2026-DIR');
    setAuthError('');
    onLoginSuccess(directorPreset);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setAuthError('');

    setTimeout(() => {
      setIsAuthenticating(false);
      const cleanUser = username.trim().toLowerCase();
      const isMatch = (cleanUser === directorPreset.demoUsername.toLowerCase() || 
                       cleanUser === directorPreset.email.toLowerCase() ||
                       cleanUser.includes('director')) &&
                      (password === directorPreset.demoPassword || password.length >= 4);

      if (isMatch) {
        onLoginSuccess(directorPreset);
      } else {
        setAuthError('Directorate clearance failed. Please verify credentials or utilize the 1-Click Fast Pass clearance below.');
      }
    }, 450);
  };

  return (
    <div className="min-h-screen bg-[#030F1C] text-slate-100 flex flex-col justify-between relative overflow-hidden py-8">
      {/* Ambient Directorate Gold Glow */}
      <div className="absolute top-10 left-1/3 w-96 h-96 bg-[#7A470E]/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#0B2644]/50 rounded-full blur-3xl pointer-events-none" />

      {/* Top Breadcrumb Nav */}
      <div className="max-w-4xl mx-auto px-4 w-full mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setActiveView('lms-hub')}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#7A470E] text-white text-xs font-bold font-sc transition-colors border border-slate-700/80 cursor-pointer shadow-xs"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-amber-300" />
            <span>← Back to Central Portals</span>
          </button>

          <div className="flex items-center space-x-2 text-xs font-mono bg-amber-950/80 border border-amber-600/40 text-amber-300 px-3 py-1.5 rounded-full">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Tier-1 Global Directorate Console · Senate Gate</span>
          </div>
        </div>
      </div>

      {/* Main Authentication Card */}
      <div className="max-w-4xl mx-auto px-4 w-full flex-1 flex flex-col justify-center">
        <div className="bg-gradient-to-b from-[#082038] via-[#06182B] to-[#04101D] border-2 border-[#B87A28]/50 rounded-3xl p-6 sm:p-10 shadow-2xl">
          
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto space-y-3 mb-8">
            <div className="inline-flex items-center justify-center p-3.5 bg-gradient-to-br from-[#0B2644] to-[#030F1C] rounded-2xl border-2 border-[#B87A28] text-amber-300 shadow-xl">
              <ShieldCheck className="w-10 h-10" />
            </div>
            <div>
              <span className="text-xs font-mono font-bold uppercase tracking-widest text-amber-400">
                Institutional Directorate & Executive Leadership
              </span>
              <h1 className="text-2xl sm:text-4xl font-black font-sc text-white tracking-wide mt-1">
                Executive Directorate Console
              </h1>
              <p className="text-xs sm:text-sm text-slate-300 font-light mt-1">
                Restricted to the Executive Director, Directorate Board, and Senate Audit Committee.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Form Column */}
            <div className="lg:col-span-7 bg-[#04101D]/80 p-6 sm:p-7 rounded-2xl border border-slate-800">
              <form onSubmit={handleFormSubmit} className="space-y-4">
                {authError && (
                  <div className="p-3 bg-red-950/80 border border-red-500/50 rounded-xl text-xs text-red-200 flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <span>{authError}</span>
                  </div>
                )}

                {/* Directorate Email */}
                <div>
                  <label className="block text-xs font-bold font-sc text-slate-200 mb-1.5">
                    Directorate Institutional Email / ID:
                  </label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="director@businessschool.aksu.edu.ng"
                      required
                      className="w-full pl-10 pr-4 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                </div>

                {/* Directorate Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold font-sc text-slate-200">
                      Directorate Master Passcode:
                    </label>
                    <span className="text-[11px] text-amber-300/80 font-mono">Tier-1 Cleared</span>
                  </div>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••••"
                      required
                      className="w-full pl-10 pr-10 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Terminal 2FA Token */}
                <div>
                  <label className="block text-xs font-bold font-sc text-slate-200 mb-1.5">
                    Senate Directorate Terminal Token (2FA):
                  </label>
                  <div className="relative">
                    <Key className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={directorateToken}
                      onChange={(e) => setDirectorateToken(e.target.value)}
                      placeholder="AKSU-EXEC-2026-DIR"
                      className="w-full pl-10 pr-4 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-amber-200 font-mono focus:outline-none focus:border-amber-400"
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isAuthenticating}
                    className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#7A470E] to-[#B87A28] hover:from-[#5C3306] hover:to-[#7A470E] text-white font-sc font-bold text-xs flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transition-all cursor-pointer disabled:opacity-50"
                  >
                    <span>{isAuthenticating ? 'Verifying Senate Clearance...' : 'Authenticate & Enter Director Console'}</span>
                    <ArrowRight className="w-4 h-4 text-amber-200" />
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column: Persona Profile & Fast Pass */}
            <div className="lg:col-span-5 space-y-4">
              
              {/* Evaluator Fast Pass */}
              <div className="bg-[#0B2644]/40 p-5 rounded-2xl border border-[#B87A28]/40">
                <div className="flex items-center space-x-2 text-xs font-bold font-sc text-amber-300 mb-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span>Evaluator 1-Click Fast Pass:</span>
                </div>
                <p className="text-[11px] text-slate-300 mb-4 leading-relaxed">
                  Bypass manual typing. Click below to sign in directly as the Executive Director:
                </p>

                <button
                  type="button"
                  onClick={handle1ClickFastPass}
                  className="w-full text-left p-3 rounded-xl bg-[#06182B] hover:bg-[#7A470E] border border-[#B87A28]/60 hover:border-amber-400 transition-all flex items-center justify-between group cursor-pointer shadow-md"
                >
                  <div className="flex items-center space-x-3">
                    <img 
                      src={directorPreset.avatar} 
                      alt={directorPreset.name} 
                      className="w-10 h-10 rounded-xl object-cover border border-[#B87A28]" 
                    />
                    <div>
                      <div className="text-xs font-bold text-white group-hover:text-amber-200 font-sc">
                        {directorPreset.name}
                      </div>
                      <div className="text-[10px] text-amber-300/90 font-mono">
                        {directorPreset.qualifications} · {directorPreset.title}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-amber-400 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* Scoped Authority Summary */}
              <div className="p-4 rounded-2xl bg-[#06182B] border border-slate-800 space-y-2 text-xs">
                <span className="font-bold text-slate-200 block font-sc">
                  Directorate Console Permissions:
                </span>
                <div className="space-y-1.5 text-[11px] text-slate-400">
                  <div className="flex items-start space-x-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span>Executive applicant screening approval & letter issuance</span>
                  </div>
                  <div className="flex items-start space-x-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span>Real-time tuition settlement and revenue analytics</span>
                  </div>
                  <div className="flex items-start space-x-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span>Doctoral defense clearance & Senate graduation rolls</span>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="max-w-4xl mx-auto px-4 w-full mt-6 text-center text-xs text-slate-400">
        <span className="font-mono text-[11px]">
          Akwa Ibom State University Executive Business School · Directorate Senate Desk
        </span>
      </div>
    </div>
  );
};

export default DirectorAuthPage;
